<script setup lang="ts">

import ActionTrigger, {type ActionSummary} from "@/components/ActionArea/ActionTrigger/ActionTrigger.vue";
import {useI18n} from "vue-i18n";

const {t} = useI18n();

interface Props {
  selectedAction?: ActionSummary | null;
  previewAction?: ActionSummary | null;
  tacticalMovementPhase?: boolean;
  inTacticalPreparation?: boolean;
  chargesPlaced?: number;
  chargeCount?: number;
  waiting?: boolean;
  inFight?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  selectedAction: null,
  previewAction: null,
  tacticalMovementPhase: false,
  inTacticalPreparation: false,
  chargesPlaced: 0,
  chargeCount: 1,
  waiting: false,
  inFight: false
});

const emit = defineEmits<{
  nextTurnTriggered: [];
  safePlaceTriggered: [];
  rollDiceTriggered: [];
  backpackTriggered: [];
  endTacticalMove: [];
}>();

</script>

<template>
  <!--  Group of Composed components with responsive positioning -->
  <div class="trigger__group__holder">
    <!-- EndTurn 100% - Big, Bottom Left -->
    <!-- Shows tactical movement icon when in tactical movement phase with no action selected -->
    <ActionTrigger
      :image="selectedAction || previewAction ? null : 'EndTurn'"
      :selected-action="selectedAction"
      :preview-action="previewAction"
      :tactical-movement-phase="tacticalMovementPhase"
      :in-tactical-preparation="inTacticalPreparation"
      :charges-placed="chargesPlaced"
      :charge-count="chargeCount"
      :waiting="waiting"
      :in-fight="inFight"
      :title="t('actionTriggers.endTurn')"
      class="trigger__cta trigger__next-turn"
      @select="$emit('nextTurnTriggered')"
      @end-tactical-move="$emit('endTacticalMove')"
    />
    <!-- Backpack — Medium, Bottom Right -->
    <ActionTrigger image="Backpack" :title="t('actionTriggers.backpack')" class="trigger__cta trigger__backpack" @select="$emit('backpackTriggered')"/>
    <!-- RollDice 50% Small, Top Left -->
    <ActionTrigger image="RollDice" :title="t('actionTriggers.rollDice')" class="trigger__cta trigger__roll-dice" @select="$emit('rollDiceTriggered')"/>
  </div>
</template>

<style scoped>
.trigger__group__holder {
  position: relative;
  width: 100%;
}

.trigger__cta {
  position: absolute;
  transition: transform 0.2s ease-in-out;
}

/* Big component - Bottom Left */
.trigger__next-turn {
  width: 80%;
  aspect-ratio: 1;
  bottom: 0;
  left: 0;
  z-index: 1;
}

/* Medium component - Bottom Right */
.trigger__backpack {
  width: 70%;
  aspect-ratio: 1;
  bottom: 0;
  margin-bottom: -8%;
  z-index: 0;
  left: 62%;
}

/* Small component - Top Left.
   Sits above the next-turn ring and on a higher z-index, so any overlap of the
   two hit boxes silently steals clicks from End Turn / casting an armed skill.
   This offset is what keeps them apart — verify hit areas if it changes. */
.trigger__roll-dice {
  width: 48%;
  aspect-ratio: 1;
  bottom: 48%;
  left: 2%;
  z-index: 3;
}

</style>